import {
  ArrowDownUp, Check, ChevronDown, ChevronLeft, ChevronRight, Copy, Filter, LayoutGrid, List, MoreHorizontal, Pencil, Plus,
  Rows2, Rows3, Settings, SlidersHorizontal, SquareKanban, Trash2, Users, X,
} from "lucide-react";
import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { contactName, dialStateChoices, dialText, stageGroup, stageSectionOf, stageSlugs, stageText } from "@/lib/contacts";
import { sourceText } from "@/lib/opportunities";
import { CONTACTS_PAGE_SIZE, useContacts, type ContactListFilters } from "@/lib/queries";
import {
  CARD_FIELD_TEXT, CARD_FIELDS, FILTER_CHOICES, FILTER_FIELDS, FILTER_TEXT, LIST_SORTS, SORT_TEXT,
  QUICK_CHIPS, appliedCount, chipOn, filterProblems, isComplete, sortApplied, splitTabs, toggleChip, toggleField,
  type CardField, type Density, type FilterField, type ListFilter, type ListSort, type ListSortField, type ListView,
  type SmartList,
} from "@/lib/smart-lists";
import { STAGE_SECTIONS, sectionText } from "@/lib/stages";
import { cn, formatDateTime, formatPhoneNumber, relativeTime } from "@/lib/utils";

/**
 * GoHighLevel's smart lists over Opportunities (`lib/smart-lists.ts`): the tabs, "+ List" with its
 * Board view and List view, the Advanced filters and Sort buttons with their panels, the list's own
 * settings, and the table the List view draws. The page owns which list is open and saves them.
 */

// --- the tabs ------------------------------------------------------------------------------------

export function ListTabs({
  lists, active, onOpen, onAdd, onEdit, onCustomize,
}: {
  lists: readonly SmartList[];
  active: string;
  onOpen: (id: string) => void;
  onAdd: (view: ListView) => void;
  onEdit: (list: SmartList) => void;
  onCustomize: () => void;
}) {
  const { shown, more } = splitTabs(lists, active);
  return (
    <div className="flex items-center gap-1 border-b border-border">
      <div className="flex min-w-0 items-center gap-1 overflow-x-auto scrollbar-subtle">
        {shown.map((l) => {
          const on = l.id === active;
          const Icon = l.view === "board" ? SquareKanban : List;
          return (
            <div key={l.id} className="flex shrink-0 items-center">
              <button
                type="button"
                onClick={() => onOpen(l.id)}
                title={l.shared ? `Shared with the team${l.ownerName ? ` by ${l.ownerName}` : ""}` : undefined}
                className={cn(
                  "-mb-px flex items-center gap-2 border-b-2 px-3 pb-3 pt-2 text-sm transition-colors",
                  on ? "border-accent-9 font-medium text-accent-11" : "border-transparent text-text-muted hover:text-text",
                )}
                aria-current={on ? "page" : undefined}
              >
                <Icon className="size-4" aria-hidden />
                {l.name}
                {l.shared && <Users className="size-3.5 text-text-subtle" aria-label="Shared with the team" />}
              </button>
              {on && l.canEdit && (
                <button
                  type="button"
                  onClick={() => onEdit(l)}
                  className="-ml-1.5 mb-1 rounded p-1 text-text-subtle hover:bg-surface-hover hover:text-text"
                  aria-label={`Settings for ${l.name}`}
                >
                  <MoreHorizontal className="size-3.5" />
                </button>
              )}
            </div>
          );
        })}
        {more.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="mb-1.5 flex shrink-0 items-center gap-1.5 rounded-md bg-surface-sunken px-3 py-1.5 text-sm font-medium text-accent-11 hover:bg-surface-hover">
                {more.length} More
                <ChevronDown className="size-4" aria-hidden />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {more.map((l) => (
                <DropdownMenuItem key={l.id} onSelect={() => onOpen(l.id)}>
                  {l.view === "board" ? <SquareKanban /> : <List />}
                  {l.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <span className="mx-1 mb-2 h-4 w-px shrink-0 bg-border" aria-hidden />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className="mb-1.5 flex shrink-0 items-center gap-1 rounded px-2 py-1.5 text-sm text-text-muted hover:text-text">
            <Plus className="size-4" aria-hidden />
            List
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72 p-0">
          <ViewChoice
            icon={SquareKanban}
            title="Board view"
            what="View opportunities as cards in each stage."
            onSelect={() => onAdd("board")}
          />
          <DropdownMenuSeparator className="my-0" />
          <ViewChoice
            icon={List}
            title="List view"
            what="View opportunities in a table format."
            onSelect={() => onAdd("list")}
          />
        </DropdownMenuContent>
      </DropdownMenu>
      <button
        type="button"
        onClick={onCustomize}
        className="mb-1.5 ml-auto flex shrink-0 items-center gap-1.5 rounded px-2 py-1.5 text-sm font-medium text-text hover:bg-surface-hover"
      >
        <SlidersHorizontal className="size-4" aria-hidden />
        Customize list
      </button>
    </div>
  );
}

/** Comfortable or compact cards, a list's own. */
export function DensityToggle({ density, onChange }: { density: Density; onChange: (density: Density) => void }) {
  const option = (value: Density, Icon: typeof List, label: string) => (
    <button
      type="button"
      aria-pressed={density === value}
      aria-label={label}
      title={label}
      onClick={() => onChange(value)}
      className={cn(
        "flex size-8 items-center justify-center rounded transition-colors",
        density === value ? "bg-surface text-accent-11 shadow-xs" : "text-text-muted hover:text-text",
      )}
    >
      <Icon className="size-4" aria-hidden />
    </button>
  );
  return (
    <div className="flex shrink-0 items-center gap-0.5 rounded-md bg-surface-sunken p-0.5" role="group" aria-label="Card size">
      {option("comfortable", Rows3, "Comfortable cards")}
      {option("compact", Rows2, "Compact cards")}
    </div>
  );
}

/** The one-click filters over the list (`QUICK_CHIPS`): each adds or removes one filter row. */
export function QuickChips({ filters, onChange }: { filters: readonly ListFilter[]; onChange: (filters: ListFilter[]) => void }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Quick filters">
      <span className="mr-1 text-xs text-text-muted">Quick:</span>
      {QUICK_CHIPS.map((chip) => {
        const on = chipOn(filters, chip);
        return (
          <button
            key={chip.id}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(toggleChip(filters, chip))}
            className={cn(
              "inline-flex h-7 items-center gap-1 rounded-full border px-2.5 text-xs transition-colors",
              on
                ? "border-accent-7 bg-accent-9 text-white hover:bg-accent-10"
                : "border-border bg-surface text-text hover:bg-surface-hover",
            )}
          >
            {on && <Check className="size-3" aria-hidden />}
            {chip.label}
          </button>
        );
      })}
    </div>
  );
}

/** "Manage fields": which facts a card shows under the name. */
export function ManageFields({
  fields, onChange,
}: {
  fields: readonly CardField[];
  onChange: (fields: CardField[]) => void;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="flex h-10 shrink-0 items-center gap-1.5 rounded-md px-2 text-sm font-medium text-text hover:bg-surface-hover">
          <Settings className="size-4" aria-hidden />
          Manage fields
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2">
        <p className="px-2 pb-2 pt-1 text-xs font-medium text-text-muted">Card fields · {fields.length} selected</p>
        {CARD_FIELDS.map((f) => (
          <label key={f} className="flex cursor-pointer items-center gap-2.5 rounded px-2 py-1.5 text-sm text-text hover:bg-surface-hover">
            <input type="checkbox" className="accent-accent-9" checked={fields.includes(f)} onChange={() => onChange(toggleField(fields, f))} />
            {CARD_FIELD_TEXT[f]}
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function ViewChoice({
  icon: Icon, title, what, onSelect,
}: {
  icon: typeof List;
  title: string;
  what: string;
  onSelect: () => void;
}) {
  return (
    <DropdownMenuItem onSelect={onSelect} className="items-start gap-3 rounded-none px-4 py-3 [&_svg]:size-4.5">
      <Icon className="mt-0.5" aria-hidden />
      <span>
        <span className="block text-sm text-text">{title}</span>
        <span className="block text-xs text-text-muted">{what}</span>
      </span>
    </DropdownMenuItem>
  );
}

// --- the buttons over the list ---------------------------------------------------------------------

export function FilterButtons({
  list, onFilters, onSort,
}: {
  list: SmartList;
  onFilters: () => void;
  onSort: (sort: ListSort) => void;
}) {
  const count = appliedCount(list);
  const sorted = sortApplied(list.sort);
  return (
    <div className="flex items-center gap-2">
      <PillButton on={count > 0} onClick={onFilters}>
        <Filter aria-hidden />
        Advanced filters{count > 0 && ` (${count})`}
      </PillButton>
      <SortPopover sort={list.sort} view={list.view} onChange={onSort}>
        <PillButton on={sorted}>
          <ArrowDownUp aria-hidden />
          Sort{sorted && " (1)"}
        </PillButton>
      </SortPopover>
    </div>
  );
}

const PillButton = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement> & { on?: boolean }>(
  function PillButton({ on, className, ...props }, ref) {
    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          "inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-sm transition-colors [&_svg]:size-3.5",
          on
            ? "border-accent-7 bg-accent-2 text-accent-11 hover:bg-accent-3"
            : "border-border bg-surface text-text hover:bg-surface-hover",
          className,
        )}
        {...props}
      />
    );
  },
);

function SortPopover({
  sort, view, onChange, children,
}: {
  sort: ListSort;
  view: ListView;
  onChange: (sort: ListSort) => void;
  children: React.ReactNode;
}) {
  // The board's columns are the stages, so it is not ordered by one.
  const fields = LIST_SORTS.filter((f) => view === "list" || f !== "stage");
  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-72 space-y-3 p-4">
        <p className="text-sm font-medium text-text">Sort by</p>
        <Select value={sort.field} onValueChange={(v) => onChange({ ...sort, field: v as ListSortField })}>
          <SelectTrigger className="h-9 text-sm" aria-label="Sort field"><SelectValue /></SelectTrigger>
          <SelectContent>
            {fields.map((f) => <SelectItem key={f} value={f}>{SORT_TEXT[f]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sort.order} onValueChange={(v) => onChange({ ...sort, order: v as ListSort["order"] })}>
          <SelectTrigger className="h-9 text-sm" aria-label="Sort order"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="asc">{sort.field === "created" ? "Oldest first" : "Ascending (A–Z)"}</SelectItem>
            <SelectItem value="desc">{sort.field === "created" ? "Newest first" : "Descending (Z–A)"}</SelectItem>
          </SelectContent>
        </Select>
      </PopoverContent>
    </Popover>
  );
}

// --- the side panels -------------------------------------------------------------------------------

/** GoHighLevel's right-hand panel: a title and a close, a body, and the actions at the foot. */
function SidePanel({
  open, onOpenChange, title, children, footer,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: React.ReactNode;
  footer: React.ReactNode;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer" className="max-w-[34rem] rounded-lg" overlayClassName="bg-gray-12/10 backdrop-blur-none">
        <div className="shrink-0 border-b border-border px-6 py-5 pr-20">
          <DialogTitle className="text-lg font-medium text-text">{title}</DialogTitle>
          <DialogDescription className="sr-only">{title} for this list.</DialogDescription>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5 scrollbar-subtle">{children}</div>
        <div className="flex shrink-0 items-center gap-2 border-t border-border px-6 py-4">{footer}</div>
      </DialogContent>
    </Dialog>
  );
}

/** Advanced filters: a row per filter, each a field, how it tests, and the value it wants. */
export function FiltersPanel({
  open, onOpenChange, filters, onApply,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: readonly ListFilter[];
  onApply: (filters: ListFilter[]) => void;
}) {
  const [draft, setDraft] = React.useState<ListFilter[]>([]);
  const [tried, setTried] = React.useState(false);
  React.useEffect(() => {
    if (!open) return;
    setDraft(filters.length ? filters.map((f) => ({ ...f, values: [...f.values] })) : [{ field: "stage", values: [] }]);
    setTried(false);
  }, [open, filters]);

  const problems = filterProblems(draft);
  const setRow = (i: number, row: ListFilter) => setDraft((d) => d.map((r, j) => (j === i ? row : r)));
  const apply = () => {
    setTried(true);
    if (problems.some(Boolean)) return;
    onApply(draft.filter(isComplete));
    onOpenChange(false);
  };

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      title="Advanced filters"
      footer={
        <>
          <Button variant="ghost" className="mr-auto" onClick={() => setDraft([])}>Clear all</Button>
          <Button size="lg" className="px-5" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="lg" variant="default" className="px-5" onClick={apply}>Apply</Button>
        </>
      }
    >
      <div className="space-y-3">
        {draft.map((row, i) => (
          <FilterRow
            key={i}
            row={row}
            problem={tried ? problems[i] ?? null : null}
            onChange={(r) => setRow(i, r)}
            onRemove={() => setDraft((d) => d.filter((_, j) => j !== i))}
          />
        ))}
        <Button onClick={() => setDraft((d) => [...d, { field: "stage", values: [] }])}>
          <Plus />
          Add filter
        </Button>
      </div>
    </SidePanel>
  );
}

function FilterRow({
  row, problem, onChange, onRemove,
}: {
  row: ListFilter;
  problem: string | null;
  onChange: (row: ListFilter) => void;
  onRemove: () => void;
}) {
  const text = FILTER_TEXT[row.field];
  return (
    <div className="space-y-2 rounded-lg border border-border bg-surface-sunken/60 p-3">
      <div className="grid grid-cols-2 gap-2">
        <Select value={row.field} onValueChange={(v) => onChange({ field: v as FilterField, values: [] })}>
          <SelectTrigger className="h-10 bg-surface text-sm" aria-label="Filter field"><SelectValue /></SelectTrigger>
          <SelectContent>
            {FILTER_FIELDS.map((f) => (
              <SelectItem key={f} value={f}>
                {FILTER_TEXT[f].label}{f.startsWith("created_") ? ` (${FILTER_TEXT[f].operator.toLowerCase()})` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex h-10 items-center rounded-md border border-border bg-surface px-3 text-sm text-text">
          {text.operator}
        </div>
      </div>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <FilterValue row={row} invalid={Boolean(problem)} onChange={(values) => onChange({ ...row, values })} />
          {problem && <p className="mt-1 text-xs text-danger-11">{problem}</p>}
        </div>
        <Button size="icon" className="size-10 bg-surface" aria-label="Remove filter" onClick={onRemove}>
          <Trash2 />
        </Button>
      </div>
    </div>
  );
}

function FilterValue({
  row, invalid, onChange,
}: {
  row: ListFilter;
  invalid: boolean;
  onChange: (values: string[]) => void;
}) {
  const field = row.field;
  const kind = FILTER_TEXT[field].kind;
  const trigger = cn("h-10 bg-surface text-sm", invalid && "border-danger-9");
  if (kind === "stages") return <StagesValue values={row.values} invalid={invalid} onChange={onChange} />;
  if (kind === "text") {
    return (
      <Input
        className={trigger}
        value={row.values[0] ?? ""}
        placeholder={field === "state" ? "Two letters, like CA" : "Like website or facebook"}
        invalid={invalid}
        onChange={(e) => onChange([e.target.value])}
      />
    );
  }
  if (kind === "date") {
    return <Input className={trigger} type="date" value={row.values[0] ?? ""} invalid={invalid} onChange={(e) => onChange([e.target.value])} />;
  }
  const choices = field === "dial_state" ? dialStateChoices(row.values[0]) : FILTER_CHOICES[field] ?? [];
  return (
    <Select value={row.values[0] ?? ""} onValueChange={(v) => onChange([v])}>
      <SelectTrigger className={trigger}><SelectValue placeholder="Please select" /></SelectTrigger>
      <SelectContent>
        {choices.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

/** Stage "is any of": the picked stages as chips, and a picker for one more. */
function StagesValue({
  values, invalid, onChange,
}: {
  values: string[];
  invalid: boolean;
  onChange: (values: string[]) => void;
}) {
  const slugs = stageSlugs().filter((s) => !values.includes(s));
  return (
    <div className={cn("flex min-h-10 flex-wrap items-center gap-1.5 rounded-md border bg-surface px-2 py-1.5", invalid ? "border-danger-9" : "border-border")}>
      {values.map((s) => (
        <Badge key={s} tone="accent" className="gap-1">
          {stageText(s).label}
          <button type="button" aria-label={`Remove ${stageText(s).label}`} onClick={() => onChange(values.filter((v) => v !== s))}>
            <X className="size-3" />
          </button>
        </Badge>
      ))}
      {slugs.length > 0 && (
        <Select value="" onValueChange={(v) => onChange([...values, v])}>
          <SelectTrigger className="h-7 w-auto min-w-32 border-0 bg-transparent px-1 text-sm shadow-none" aria-label="Add a stage">
            <SelectValue placeholder={values.length ? "Add another" : "Please select"} />
          </SelectTrigger>
          <SelectContent>
            {STAGE_SECTIONS.map((group) => {
              const inGroup = slugs.filter((s) => stageSectionOf(s) === group);
              if (!inGroup.length) return null;
              return (
                <SelectGroup key={group}>
                  <SelectLabel>{sectionText(group).label}</SelectLabel>
                  {inGroup.map((s) => <SelectItem key={s} value={s}>{stageText(s).label}</SelectItem>)}
                </SelectGroup>
              );
            })}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

/**
 * "Add smart list", and a saved list's settings: its name, its filters and sort, and Duplicate and
 * Delete. A new list is saved on Create; a saved one on Save.
 *
 * Sharing is deliberately not offered here. `draft.shared` is still carried through every edit
 * untouched, so a list already shared with the tenant is not silently un-shared by someone
 * renaming it — the flag is only ever changed through the API.
 */
export function ListSettingsPanel({
  open, onOpenChange, list, isNew, onSave, onDuplicate, onDelete,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  list: SmartList | null;
  isNew: boolean;
  onSave: (list: SmartList, isNew: boolean) => void;
  onDuplicate: (list: SmartList) => void;
  onDelete: (list: SmartList) => void;
}) {
  const [draft, setDraft] = React.useState<SmartList | null>(list);
  const [filtering, setFiltering] = React.useState(false);
  React.useEffect(() => {
    if (open) setDraft(list);
  }, [open, list]);
  if (!draft) return null;
  const count = appliedCount(draft);
  const sorted = sortApplied(draft.sort);
  const save = () => {
    const name = draft.name.trim();
    if (!name) return;
    onSave({ ...draft, name }, isNew);
    onOpenChange(false);
  };

  return (
    <>
      <SidePanel
        open={open && !filtering}
        onOpenChange={onOpenChange}
        title={isNew ? "Add smart list" : "Smart list settings"}
        footer={
          <>
            <Button size="lg" className="ml-auto px-5" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button size="lg" variant="default" className="px-5" disabled={!draft.name.trim()} onClick={save}>
              {isNew ? "Create" : "Save"}
            </Button>
          </>
        }
      >
        <div className="space-y-1">
          <Input
            aria-label="List name"
            className="h-11 text-sm"
            leading={<Pencil />}
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            autoFocus
          />
          <SettingRow icon={Filter} label="Advanced filters" value={count ? `${count} applied` : "None"} onClick={() => setFiltering(true)} />
          <SortSettingRow draft={draft} sorted={sorted} onChange={(sort) => setDraft({ ...draft, sort })} />
          <SettingRow icon={draft.view === "board" ? LayoutGrid : List} label="View" value={draft.view === "board" ? "Board" : "List"} />
          {!isNew && !draft.mine && draft.ownerName && (
            <p className="px-2 pt-1 text-xs text-text-muted">Made by {draft.ownerName}.</p>
          )}
          {!isNew && (
            <div className="mt-4 border-t border-border pt-4">
              <SettingRow icon={Copy} label="Duplicate" onClick={() => { onDuplicate(draft); onOpenChange(false); }} />
              <SettingRow icon={Trash2} label="Delete list" danger onClick={() => { onDelete(draft); onOpenChange(false); }} />
            </div>
          )}
        </div>
      </SidePanel>
      <FiltersPanel
        open={open && filtering}
        onOpenChange={(o) => !o && setFiltering(false)}
        filters={draft.filters}
        onApply={(filters) => setDraft({ ...draft, filters })}
      />
    </>
  );
}

function SettingRow({
  icon: Icon, label, value, onClick, danger,
}: {
  icon: typeof List;
  label: string;
  value?: string;
  onClick?: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={cn(
        "flex w-full items-center gap-3 rounded-md px-2 py-3.5 text-left text-base transition-colors",
        onClick ? "hover:bg-surface-hover" : "cursor-default",
        danger ? "text-danger-11" : "text-text",
      )}
    >
      <Icon className="size-4.5 shrink-0" aria-hidden />
      <span className="flex-1">{label}</span>
      {value && <span className="text-sm text-text-muted">{value}</span>}
      {onClick && <ChevronRight className="size-4 text-text-subtle" aria-hidden />}
    </button>
  );
}

function SortSettingRow({ draft, sorted, onChange }: { draft: SmartList; sorted: boolean; onChange: (sort: ListSort) => void }) {
  return (
    <SortPopover sort={draft.sort} view={draft.view} onChange={onChange}>
      <button type="button" className="flex w-full items-center gap-3 rounded-md px-2 py-3.5 text-left text-base text-text hover:bg-surface-hover">
        <ArrowDownUp className="size-4.5 shrink-0" aria-hidden />
        <span className="flex-1">Sort by</span>
        <span className="text-sm text-text-muted">{sorted ? "1 applied" : "Default"}</span>
        <ChevronRight className="size-4 text-text-subtle" aria-hidden />
      </button>
    </SortPopover>
  );
}

// --- the List view ---------------------------------------------------------------------------------

/** The List view: `GET /leads` a page at a time, a row per opportunity, the header's sort the list's. */
export function OpportunityTable({
  filters, sort, onSort, onOpen,
}: {
  filters: ContactListFilters;
  sort: ListSort;
  onSort: (sort: ListSort) => void;
  onOpen: (leadId: string) => void;
}) {
  const [page, setPage] = React.useState(0);
  const key = JSON.stringify(filters);
  React.useEffect(() => setPage(0), [key]);
  const rows = useContacts(filters, page);
  const total = rows.data?.total ?? null;
  const pages = total === null ? null : Math.max(1, Math.ceil(total / CONTACTS_PAGE_SIZE));

  const header = (field: ListSortField | null, label: string, className?: string) => (
    <TableHead className={cn("normal-case tracking-normal", className)}>
      {field ? (
        <button
          type="button"
          className="inline-flex items-center gap-1 font-semibold text-text hover:text-accent-11"
          onClick={() => onSort({ field, order: sort.field === field && sort.order === "asc" ? "desc" : "asc" })}
        >
          {label}
          <ArrowDownUp className={cn("size-3", sort.field === field ? "text-accent-11" : "text-text-subtle")} aria-hidden />
        </button>
      ) : (
        <span className="font-semibold text-text">{label}</span>
      )}
    </TableHead>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-surface">
      <div className="min-h-0 flex-1 overflow-auto scrollbar-subtle">
        {rows.isPending ? (
          <TableSkeleton rows={10} cols={7} />
        ) : rows.error ? (
          <ErrorState error={rows.error} title="Could not load the opportunities" action={<Button onClick={() => void rows.refetch()}>Try again</Button>} />
        ) : rows.data.items.length === 0 ? (
          <EmptyState icon={List} title="No opportunities" description="Nothing matches this list's filters." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                {header("name", "Opportunity name", "min-w-52")}
                {header("stage", "Stage")}
                {header(null, "Pipeline")}
                {header(null, "Source")}
                {header("phone", "Phone")}
                {header(null, "Email")}
                {header(null, "Dialer")}
                {header("created", "Created")}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.data.items.map((r) => {
                const source = sourceText(r.source);
                const dial = dialText(r.dial, r.membership?.outcome_rep);
                return (
                  <TableRow key={r.id} interactive onClick={() => onOpen(r.id)}>
                    <TableCell className="font-medium text-text">{contactName(r) ?? "No name"}</TableCell>
                    <TableCell><Badge tone={stageText(r.stage).tone} size="sm">{stageText(r.stage).label}</Badge></TableCell>
                    <TableCell className="text-text-muted">{r.firm?.label ?? "No firm"}</TableCell>
                    <TableCell>{source.label}</TableCell>
                    <TableCell className="whitespace-nowrap font-mono text-xs">{formatPhoneNumber(r.phone)}</TableCell>
                    <TableCell className="max-w-56 truncate text-text-muted">{r.email ?? "—"}</TableCell>
                    <TableCell><Badge tone={dial.tone} size="sm">{dial.label}</Badge></TableCell>
                    <TableCell className="whitespace-nowrap text-text-muted" title={formatDateTime(r.created_at)}>{relativeTime(r.created_at)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
      <div className="flex shrink-0 items-center justify-between border-t border-border px-4 py-2.5 text-sm text-text-muted">
        <span>{pages !== null ? `Page ${page + 1} of ${pages}` : " "}</span>
        <span className="flex items-center gap-2">
          {total !== null && <span>{total.toLocaleString()} {total === 1 ? "opportunity" : "opportunities"}</span>}
          <Button size="icon-sm" aria-label="Previous page" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            <ChevronLeft />
          </Button>
          <Button size="icon-sm" aria-label="Next page" disabled={pages === null || page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>
            <ChevronRight />
          </Button>
        </span>
      </div>
    </div>
  );
}
